import {
  EventGetListRequestSchema,
  type EventGetListRequestType,
  type EventType,
} from "../schemas/EventSchemas.ts";
import {
  fetchAndCollate,
  getFilterObject,
  Resources,
  type CreateOneRequestType,
  type CreateOneResponseType,
  type DeleteOneRequestType,
  type DeleteOneResponseType,
  type GetListRequestType,
  type GetListResponseType,
  type GetOneRequestType,
  type GetOneResponseType,
  type ResourceAdapterToAdapterQueryKeysType,
  type ResourceAdapterType,
  type UpdateOneRequestType,
  type UpdateOneResponseType,
} from "../util/index.ts";
import { restAPI } from "./api.ts";
import {
  SavedFiltersAdapter,
  type SavedFiltersAdapterType,
} from "./composable/index.ts";

export interface EventAdapterType
  extends Pick<
    ResourceAdapterType<EventType>,
    "getOne" | "getList" | "createOne" | "updateOne" | "deleteOne"
  > {
  getOne: (params: GetOneRequestType) => Promise<GetOneResponseType<EventType>>;
  getList: (
    params: GetListRequestType
  ) => Promise<GetListResponseType<EventType>>;
  createOne: (
    params: CreateOneRequestType<EventType>
  ) => Promise<CreateOneResponseType<EventType>>;
  updateOne: (
    params: UpdateOneRequestType<EventType>
  ) => Promise<UpdateOneResponseType<EventType>>;
  deleteOne: (
    params: DeleteOneRequestType<EventType>
  ) => Promise<DeleteOneResponseType>;
  alerts: {
    getList: (params: {
      eventId: string;
    }) => Promise<Array<{ event_id: string; alert_id: string }>>;
    createOne: (params: { eventId: string; alertId: string }) => Promise<{
      success: boolean;
    }>;
    deleteOne: (params: {
      eventId: string;
      alertId: string;
    }) => Promise<DeleteOneResponseType>;
  };
  measurementSeries: {
    createOne: (params: { eventId: string; seriesId: string }) => Promise<{
      success: boolean;
    }>;
    deleteOne: (params: {
      eventId: string;
      seriesId: string;
    }) => Promise<DeleteOneResponseType>;
  };
  assets: {
    createOne: (params: { eventId: string; assetId: string }) => Promise<{
      success: boolean;
    }>;
    deleteOne: (params: {
      eventId: string;
      assetId: string;
    }) => Promise<DeleteOneResponseType>;
  };
  formSubmissions: {
    createOne: (params: {
      eventId: string;
      formSubmissionId: string;
    }) => Promise<{ success: boolean }>;
    deleteOne: (params: {
      eventId: string;
      formSubmissionId: string;
    }) => Promise<DeleteOneResponseType>;
  };
  savedFilters: SavedFiltersAdapterType<
    typeof EventGetListRequestSchema.shape.filters
  >;
}

const endpoint = "/events";

export const EventAdapter: EventAdapterType = {
  /**
   * Get a single Event by ID
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/get_event
   */
  getOne: ({ id }: GetOneRequestType) =>
    restAPI.nodeAPI
      .GET<EventType>({ endpoint: `${endpoint}/${id}` })
      .then((data) => ({ data })),

  /**
   * List Events using the search endpoint with filter object
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/list_events
   */
  getList: ({
    page,
    pageSize,
    sortBy,
    sortDirection,
    filters,
    meta,
  }: EventGetListRequestType) =>
    fetchAndCollate<EventType>(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.POST<GetListResponseType<EventType>>({
          endpoint: `${endpoint}/search`,
          query: meta?.eventSchema
            ? { event_schema: meta.eventSchema }
            : undefined,
          body: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            filter: getFilterObject(filters),
          },
        }),
      page,
      pageSize
    ),

  /**
   * Create an Event
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/create_event
   */
  createOne: ({ data }: CreateOneRequestType<EventType>) =>
    restAPI.nodeAPI
      .POST<EventType>({ endpoint, body: data })
      .then((data) => ({ data })),

  /**
   * Update an Event
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/update_event
   */
  updateOne: ({ id, data }: UpdateOneRequestType<EventType>) =>
    restAPI.nodeAPI
      .PUT<EventType>({ endpoint: `${endpoint}/${id}`, body: data })
      .then((data) => ({ data })),

  /**
   * Delete an Event
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/delete_event
   */
  deleteOne: ({ id }: { id: string }) =>
    restAPI.nodeAPI.DELETE({ endpoint: `${endpoint}/${id}` }),

  alerts: {
    /**
     * List event/alert relationships
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/list_event_alerts
     */
    getList: ({ eventId }) =>
      restAPI.nodeAPI.GET<Array<{ event_id: string; alert_id: string }>>({
        endpoint: `${endpoint}/${eventId}/alerts`,
      }),
    /**
     * Add an event/alert relationship
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/add_event_alert
     */
    createOne: ({ eventId, alertId }) =>
      restAPI.nodeAPI
        .PUT({ endpoint: `${endpoint}/${eventId}/alerts/${alertId}` })
        .then(() => ({ success: true })),
    /**
     * Delete an event/alert relationship
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/remove_event_alert
     */
    deleteOne: ({ eventId, alertId }) =>
      restAPI.nodeAPI.DELETE({
        endpoint: `${endpoint}/${eventId}/alerts/${alertId}`,
      }),
  },

  measurementSeries: {
    /**
     * Add an event/measurement series relationship
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/add_event_measurement_series
     */
    createOne: ({ eventId, seriesId }) =>
      restAPI.nodeAPI
        .PUT({
          endpoint: `${endpoint}/${eventId}/measurement_series/${seriesId}`,
        })
        .then(() => ({ success: true })),
    /**
     * Delete an event/measurement series relationship
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/remove_event_measurement_series
     */
    deleteOne: ({ eventId, seriesId }) =>
      restAPI.nodeAPI.DELETE({
        endpoint: `${endpoint}/${eventId}/measurement_series/${seriesId}`,
      }),
  },

  assets: {
    /**
     * Add an event/asset series relationship
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/add_event_asset
     */
    createOne: ({ eventId, assetId }) =>
      restAPI.nodeAPI
        .PUT({
          endpoint: `${endpoint}/${eventId}/asset/${assetId}`,
        })
        .then(() => ({ success: true })),
    /**
     * Delete an event/asset series relationship
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/remove_event_asset
     */
    deleteOne: ({ eventId, assetId }) =>
      restAPI.nodeAPI.DELETE({
        endpoint: `${endpoint}/${eventId}/asset/${assetId}`,
      }),
  },

  formSubmissions: {
    /**
     * Add an event/form submission relationship
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/add_event_form_submission
     */
    createOne: ({ eventId, formSubmissionId }) =>
      restAPI.nodeAPI
        .PUT({
          endpoint: `${endpoint}/${eventId}/form_submissions/${formSubmissionId}`,
        })
        .then(() => ({ success: true })),
    /**
     * Delete an event/form submission relationship
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/remove_event_form_submission
     */
    deleteOne: ({ eventId, formSubmissionId }) =>
      restAPI.nodeAPI.DELETE({
        endpoint: `${endpoint}/${eventId}/form_submissions/${formSubmissionId}`,
      }),
  },

  /**
   * Event Saved Filter APIs
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/get_events_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/post_events_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/put_events_filter
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/events/delete_events_filter
   */
  savedFilters: SavedFiltersAdapter({ endpoint: "/events/filters" }),
};

export const EventAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<EventAdapterType> =
  {
    getList: (params) => [Resources.EVENT, "getList", params] as const,
    getOne: (params) => [Resources.EVENT, "getOne", params.id] as const,
    updateOne: () => [Resources.EVENT] as const,
    createOne: () => [Resources.EVENT] as const,
    deleteOne: () => [Resources.EVENT] as const,
    alerts: {
      getList: ({ eventId }) => [Resources.EVENT, "alerts", eventId] as const,
      createOne: () => [Resources.EVENT, "alerts"] as const,
      deleteOne: () => [Resources.EVENT, "alerts"] as const,
    },
    measurementSeries: {
      createOne: () => [Resources.EVENT, Resources.MEASUREMENT_SERIES] as const,
      deleteOne: () => [Resources.EVENT, Resources.MEASUREMENT_SERIES] as const,
    },
    assets: {
      createOne: () => [Resources.EVENT, "assets"] as const,
      deleteOne: () => [Resources.EVENT, "assets"] as const,
    },
    formSubmissions: {
      createOne: () => [Resources.EVENT, "formSubmissions"] as const,
      deleteOne: () => [Resources.EVENT, "formSubmissions"] as const,
    },
    savedFilters: {
      getList: (params) =>
        [Resources.EVENT, "filters", "getList", params] as const,
      updateOne: () => [Resources.EVENT, "filters"] as const,
      createOne: (_) => [Resources.EVENT, "filters"] as const,
      deleteOne: (_) => [Resources.EVENT, "filters"] as const,
    },
  };
